import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, Mic, MicOff, Volume2, VolumeX, Loader2, ExternalLink } from 'lucide-react';

/**
 * AIChatPopup — Federation unified chat popup.
 *
 * One component drives every persona: Aria, Rex/Grok, Agent Zero, any of the 56 SIMs.
 * Voice mic + voice output toggle are ALWAYS visible in the header.
 *
 * Props:
 *   persona: {
 *     id:            unique persona id            (e.g. 'rex', 'aria', '<sim-id>')
 *     name:          display name                 (e.g. 'Rex')
 *     subtitle:      role/tagline                 (e.g. 'Federation Voice · Powered by Grok')
 *     accentColor:   primary hex                  (cyan/magenta/gold)
 *     secondaryColor: secondary hex
 *     avatarUrl:     optional avatar image
 *     initial:       letter for fallback avatar
 *     endpointPath:  API path                     (e.g. '/api/rex/chat' or '/api/aria/chat' or
 *                                                 `/api/interactive/agents/<id>/interact`)
 *     systemHint:    visitor-visible hint string  (shown once at chat open)
 *     externalLink:  optional { label, url }      (e.g. { label: 'x.ai', url: 'https://x.ai' })
 *     payloadShape:  'message+history' | 'sim'    (default 'message+history')
 *   }
 *   onClose: () => void
 */
const AIChatPopup = ({ persona, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [voiceOutEnabled, setVoiceOutEnabled] = useState(true);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const scrollRef = useRef(null);
  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  // Seed greeting once
  useEffect(() => {
    setMessages([{
      role: 'assistant',
      content: `Hi — I'm ${persona.name}. ${persona.systemHint || 'How can I help?'}`,
      ts: Date.now(),
    }]);
  }, [persona.name, persona.systemHint]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const speak = useCallback((text) => {
    if (!voiceOutEnabled || typeof window === 'undefined' || !window.speechSynthesis) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 1.0;
      u.pitch = 1.0;
      window.speechSynthesis.speak(u);
    } catch (e) {
      console.warn('[AIChatPopup] TTS failed:', e);
    }
  }, [voiceOutEnabled]);

  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && window.speechSynthesis) window.speechSynthesis.cancel();
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch { /* ignore */ }
      }
    };
  }, []);

  const buildPayload = (text) => {
    if (persona.payloadShape === 'sim') {
      return JSON.stringify({ message: text, context: persona.context || {} });
    }
    // Default: message + history (works for /api/rex/chat and /api/aria/chat)
    const history = messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({ role: m.role, content: m.content }));
    return JSON.stringify({ message: text, history });
  };

  const sendMessage = async (text) => {
    const message = (text || input).trim();
    if (!message || sending) return;
    setMessages((prev) => [...prev, { role: 'user', content: message, ts: Date.now() }]);
    setInput('');
    setSending(true);
    try {
      const resp = await fetch(`${apiUrl}${persona.endpointPath}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: buildPayload(message),
      });
      const data = await resp.json();
      const reply = data.response || data.message || data.content || data.detail || 'I am here.';
      setMessages((prev) => [...prev, { role: 'assistant', content: reply, ts: Date.now() }]);
      speak(reply);
    } catch (e) {
      console.warn('[AIChatPopup] send failed:', e);
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: 'I hit a snag reaching the Federation. Please try again in a moment.',
        ts: Date.now(),
      }]);
    } finally {
      setSending(false);
    }
  };

  const toggleListening = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      alert('Voice input is not supported in this browser. Please type your message.');
      return;
    }
    if (listening && recognitionRef.current) {
      recognitionRef.current.stop();
      setListening(false);
      return;
    }
    const rec = new SR();
    rec.lang = 'en-US';
    rec.continuous = false;
    rec.interimResults = false;
    rec.onresult = (ev) => {
      const transcript = ev.results[0]?.[0]?.transcript || '';
      if (transcript) {
        setInput(transcript);
        sendMessage(transcript);
      }
    };
    rec.onerror = (e) => { console.warn('[AIChatPopup] mic err:', e); setListening(false); };
    rec.onend = () => setListening(false);
    rec.start();
    recognitionRef.current = rec;
    setListening(true);
  };

  const accent = persona.accentColor || '#00ffff';
  const secondary = persona.secondaryColor || '#ba55d3';

  return (
    <div
      data-testid="ai-chat-popup"
      role="dialog"
      aria-label={`Chat with ${persona.name}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', zIndex: 10000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
      }}
    >
      <div style={{
        width: '100%', maxWidth: '640px', height: '80vh', maxHeight: '720px',
        background: 'rgba(10, 10, 30, 0.98)',
        border: `2px solid ${accent}`,
        borderRadius: '14px',
        boxShadow: `0 0 36px ${accent}66, 0 0 60px ${secondary}22`,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0.9rem 1.1rem', borderBottom: `1px solid ${accent}30`,
          background: `linear-gradient(135deg, ${accent}12, ${secondary}10)`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', minWidth: 0 }}>
            {persona.avatarUrl ? (
              <img
          decoding="async"
          loading="lazy"
                src={persona.avatarUrl}
                alt={persona.name}
                style={{
                  width: 42, height: 42, borderRadius: '50%', objectFit: 'cover',
                  border: `2px solid ${accent}`, flexShrink: 0,
                }}
              />
            ) : (
              <div style={{
                width: 42, height: 42, borderRadius: '50%',
                background: `linear-gradient(135deg, ${accent}, ${secondary})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#000', fontWeight: 800, fontSize: '1.1rem', flexShrink: 0,
              }}>{persona.initial || persona.name?.charAt(0) || '?'}</div>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {persona.name}
              </div>
              <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {persona.subtitle}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            {persona.externalLink && (
              <a
                href={persona.externalLink.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ai-chat-external-link"
                title={`Open ${persona.externalLink.label}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.25rem',
                  padding: '0.3rem 0.55rem',
                  borderRadius: '6px',
                  border: `1px solid ${accent}40`,
                  background: 'transparent',
                  color: accent,
                  textDecoration: 'none',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                }}
              >
                <ExternalLink size={12} /> {persona.externalLink.label}
              </a>
            )}
            <button
              data-testid="ai-chat-voice-out-toggle"
              onClick={() => setVoiceOutEnabled((v) => !v)}
              title={voiceOutEnabled ? 'Mute voice replies' : 'Enable voice replies'}
              aria-pressed={voiceOutEnabled}
              style={{
                background: 'transparent', border: `1px solid ${accent}40`,
                color: voiceOutEnabled ? accent : 'rgba(255,255,255,0.4)',
                padding: '0.35rem 0.5rem', borderRadius: '6px', cursor: 'pointer',
              }}
            >
              {voiceOutEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
            </button>
            <button
              data-testid="ai-chat-mic-header"
              onClick={toggleListening}
              title={listening ? 'Stop listening' : 'Voice input (mic)'}
              aria-pressed={listening}
              style={{
                background: listening ? `linear-gradient(135deg, ${secondary}, #ff0080)` : 'transparent',
                border: `1px solid ${listening ? '#ff0080' : accent + '40'}`,
                color: listening ? '#fff' : accent,
                padding: '0.35rem 0.5rem', borderRadius: '6px', cursor: 'pointer',
              }}
            >
              {listening ? <MicOff size={14} /> : <Mic size={14} />}
            </button>
            <button
              data-testid="ai-chat-close"
              onClick={onClose}
              title="Close"
              aria-label="Close chat"
              style={{
                background: 'transparent', border: '1px solid rgba(255,80,80,0.4)',
                color: '#ff8888', padding: '0.35rem 0.5rem', borderRadius: '6px', cursor: 'pointer',
              }}
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div
          ref={scrollRef}
          data-testid="ai-chat-messages"
          style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.1rem', background: 'rgba(0,0,0,0.25)' }}
        >
          {messages.map((m) => (
            <div
              key={`${m.role}-${m.ts}`}
              style={{
                display: 'flex',
                justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                marginBottom: '0.7rem',
              }}
            >
              <div style={{
                maxWidth: '78%',
                padding: '0.6rem 0.85rem',
                borderRadius: '12px',
                background: m.role === 'user'
                  ? `linear-gradient(135deg, ${accent}22, ${secondary}22)`
                  : 'rgba(255,255,255,0.04)',
                border: `1px solid ${m.role === 'user' ? accent + '55' : secondary + '50'}`,
                color: '#fff',
                fontSize: '0.92rem',
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}>
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', padding: '0.3rem 0.25rem' }}>
              <Loader2 size={12} className="animate-spin" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 6 }} />
              {persona.name} is thinking…
            </div>
          )}
        </div>

        {/* Input */}
        <div style={{
          display: 'flex', gap: '0.5rem', padding: '0.8rem 1.1rem',
          borderTop: `1px solid ${accent}25`,
          background: 'rgba(0,0,0,0.4)',
        }}>
          <button
            data-testid="ai-chat-mic"
            onClick={toggleListening}
            title={listening ? 'Stop listening' : 'Voice input'}
            disabled={sending}
            style={{
              background: listening ? `linear-gradient(135deg, ${secondary}, #ff0080)` : `${accent}10`,
              border: `1px solid ${listening ? '#ff0080' : accent + '50'}`,
              color: listening ? '#fff' : accent,
              padding: '0 0.85rem',
              borderRadius: '8px',
              cursor: sending ? 'not-allowed' : 'pointer',
              flexShrink: 0,
            }}
          >
            {listening ? <MicOff size={16} /> : <Mic size={16} />}
          </button>
          <input
            data-testid="ai-chat-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder={`Message ${persona.name}…`}
            disabled={sending}
            style={{
              flex: 1,
              padding: '0.65rem 0.9rem',
              background: 'rgba(0,0,0,0.5)',
              border: `1px solid ${accent}40`,
              borderRadius: '8px',
              color: '#fff',
              fontSize: '0.95rem',
              outline: 'none',
            }}
          />
          <button
            data-testid="ai-chat-send"
            onClick={() => sendMessage()}
            disabled={sending || !input.trim()}
            style={{
              background: `linear-gradient(135deg, ${accent}, ${secondary})`,
              color: '#000',
              fontWeight: 700,
              padding: '0 1rem',
              borderRadius: '8px',
              border: 'none',
              cursor: (sending || !input.trim()) ? 'not-allowed' : 'pointer',
              opacity: (sending || !input.trim()) ? 0.5 : 1,
              flexShrink: 0,
            }}
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIChatPopup;
